// ConfigureEnvironmentScreen.js

// Importing various components and utilities
import React, { useState, useEffect } from 'react';
import { selectTheme } from "../theme/ThemeUtils";
import { Switch, IconButton, Tooltip, Box, FormControlLabel } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { PiComputerTower } from 'react-icons/pi'; // a react-icon, encapuslate so sx works
import TvIcon from '@mui/icons-material/Tv';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import Icon from '@mui/material/Icon';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../utils/useResponsive';
import { wmm_url } from '../utils/Utils.js';

// The main ConfigureEnvironmentScreen component
export function ConfigureEnvironmentScreen({ environmentGroup, handleRemoveScreen, handleRemoveEnvironment, ...otherProps }) {
  console.log('ConfigureEnvironmentScreen: environmentGroup:', environmentGroup);

  // Constants and state variables
  const theme = selectTheme();
  const { t } = useTranslation();
  const { isMobile, isTablet, isHD, iconSize } = useResponsive();
  const [showOnlyActive, setShowOnlyActive] = useState(true); // Default to showing only active displays

  let IconSizeMinusTwo = `${parseInt(iconSize) - 2}px`;

  let currentRow = 15;

  let totalScreens = 0; // Keep track of the total number of screens

  const [hoveredItem, setHoveredItem] = useState(null);
  console.log('ConfigureEnvironmentScreen: hoveredItem:', hoveredItem);

  // Functions handle mouse enter/leave events - using original version
  const handleMouseEnter = (id) => {
    console.log('ConfigureEnvironmentScreen: Mouse entered:', id);
    setHoveredItem(id);
  };

  const handleMouseLeave = () => {
    console.log('ConfigureEnvironmentScreen: Mouse left');
    setHoveredItem(null);
  };

  // Filter environments and screens based on the showOnlyActive state
  const filteredEnvironmentGroup = environmentGroup.map(({ environment, screens }) => {
    // Filter screens if showOnlyActive is true
    const filteredScreens = showOnlyActive 
      ? screens.filter(screen => screen.on === "1" || screen.active) 
      : [...screens];
    
    return {
      environment,
      screens: filteredScreens
    };
  }).filter(({ environment, screens }) => {
    // Keep environments that have active screens or if we're showing all
    return !showOnlyActive || screens.length > 0 || environment.alive === "1";
  });

  // The component's render method
  return (
    <>
      {/* Toggle switch for "Only Active Displays" */}
      <div 
        style={{
          gridRow: '13 / 14',
          gridColumn: '1 / 16',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingRight: '10px',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyActive}
              onChange={(e) => {
                console.log("Toggle switch clicked:", e.target.checked);
                setShowOnlyActive(e.target.checked);
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.palette.primary.main,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          }
          label={
            <div
              style={{ 
                color: theme.palette.text.primary,
                fontSize: '1.25rem',
                fontFamily: 'inherit'
              }}
            >
              {t("descriptions.showOnlyOn")}
            </div>
          }
        />
      </div>

      {/* The environmentGroup prop is mapped to a set of components */}
      {filteredEnvironmentGroup && filteredEnvironmentGroup.map(({ environment, screens }, index) => {

        // Various constants defined
        const environmentRow = currentRow; // Set the row for the current environment
        currentRow += 1; // Increment currentRow for each environment

        const isEncrypted = environment.name !== 'Web player';

        // Screen component defined
        const screenComponents = screens.map((screen, screenIndex) => {
          const screenRow = environmentRow + screenIndex * 5; // Calculate screenRow based on environmentRow and screenIndex        
          return (
            <div
              key={screen.id}
              className="screen"
              style={{
                gridRow: `${screenRow} / span 4`,
                gridColumn: '7 / 10',
                border: `2px solid ${theme.palette.primary.main}`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                height: "100%",
                width: "100%",
                backgroundColor: theme.palette.primary.contrastText,
                position: "relative" // Kept this for better hover detection
              }}
              onMouseEnter={() => handleMouseEnter(screen.id)}
              onMouseLeave={handleMouseLeave}
            >
              {/* Line 1: Switch + Disk icon */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "33.3%",
                  overflow: 'hidden',
                }}
              >
                <Tooltip title={t("action.enable")}>
                  <Switch
                    size={isMobile ? 'small' : 'medium'}
                    style={{ alignSelf: "center" }}
                    checked={screen.on === "1"}
                    classes={{
                      thumb: {
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Tooltip>
                {/* Disk icon */}
                <Tooltip title={t("action.active")}>
                    <FiberManualRecordIcon sx={{ fontSize: iconSize }} color={screen.on === "1" ? "success" : "action"} />
                </Tooltip>
              </div>

              {/* Line 2: Screen name */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                fontSize: 'IconSizeMinusTwo',
                justifyContent: 'center',
                height: '33.3%',
                overflow: 'hidden',
              }}>
                {Number(screen.width) <= 768 && <SmartphoneIcon sx={{ fontSize: IconSizeMinusTwo, color: theme.palette.primary.main }} />}
                {Number(screen.width) > 768 && Number(screen.width) <= 1024 && <TabletAndroidIcon sx={{ fontSize: IconSizeMinusTwo, color: theme.palette.primary.main }} />}
                {Number(screen.width) > 1024 && <TvIcon sx={{ fontSize: IconSizeMinusTwo, color: theme.palette.primary.main }} />}
                <div style={{ 
                  lineHeight: '1',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  textAlign: 'center',
                  fontWeight: 'normal',
                  fontSize: 'IconSizeMinusTwo'
                }}>
                  {screen.name}
                </div>
              </div>

              {/* Line 3: QR Code + Delete icon */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end", /* Changed from center to flex-end */
                  height: "33.3%",
                  padding: "0 5px 5px 5px", /* Added padding to align at bottom */
                  overflow: 'hidden',
                  visibility: hoveredItem === screen.id ? "visible" : "hidden",
                }}
              >
                {/* QR Code icon */}
                <Tooltip
                  title={
                    <div>
                      <div style={{ fontSize: '0.875rem' }}>{t("description channel for smartphones")}</div>
                      <div 
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        onClick={() => window.open(`${wmm_url}/info/?screen=${screen.id}`, '_blank')}
                      >
                        {`${wmm_url}/info/?screen=${screen.id}`}
                      </div>
                    </div>
                  }
                  arrow
                >
                  <IconButton>
                    <QrCodeIcon sx={{ fontSize: iconSize }} />
                  </IconButton>
                </Tooltip>


                {/* Delete icon */}
                <Tooltip title={t("action.delete")}>
                  <IconButton
                    onClick={() => handleRemoveScreen(screen.id)}
                  >
                    <DeleteForeverIcon sx={{ fontSize: iconSize }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        });

        currentRow += Math.max(1, screens.length) * 5; // Increment currentRow based on the number of screens in the current environment, including if 0 or 1 screen, hence environmenty

        // Environment component defined
        return (
          <React.Fragment key={`environmentGroup-${index}`}>
            {/* Your environment component code here */}
            <div
              key={environment.id}
              className={`environmentGroup ${index % 2 !== 0 ? "left" : "right"}`}
              style={{
                gridRow: `${environmentRow} / span 4`,
                gridColumn: isEncrypted ? '3 / 6' : '11 / 14',
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                height: "100%",
                position: "relative" // Kept this for better hover detection
              }}
              onMouseEnter={() => handleMouseEnter(environment.id)}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className="environment"
                style={{
                  gridRow: `${environmentRow} / span ${4 + screens.length * 5}`,
                  gridColumn: isEncrypted ? '3 / 6' : '11 / 14',
                  justifyContent: "flex-start",
                  border: isEncrypted ? `2px solid ${theme.palette.primary.main}` : `2px dotted ${theme.palette.primary.main}`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  height: "100%",
                }}
              >
                {/* Line 1: Switch + Disk icon */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "33.3%",
                    overflow: 'hidden',
                  }}
                >
                  <Switch
                    size={isMobile ? 'small' : 'medium'}
                    style={{ alignSelf: "center" }}
                    checked={environment.alive === "1"}
                    classes={{
                      thumb: {
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                  {/* Disk icon */}
                  <IconButton>
                    <FiberManualRecordIcon sx={{ fontSize: iconSize }} color={environment.alive === "1" ? "success" : "action"} />
                  </IconButton>
                </div>

                {/* Line 2: Environment name */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: 'column',
                    alignItems: "center",
                    fontSize: 'IconSizeMinusTwo',
                    justifyContent: "center",
                    whiteSpace: 'nowrap',
                    overflow: "hidden",
                    textOverflow: 'ellipsis',
                    height: '33.3%',
                  }}
                >
                  <Icon sx={{ fontSize: IconSizeMinusTwo, color: theme.palette.primary.main }}>
                    <PiComputerTower />
                  </Icon>
                  <div style={{
                    lineHeight: '1',
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    textAlign: 'center',
                    fontWeight: 'normal',
                    fontSize: 'IconSizeMinusTwo'
                  }}>
                    {environment.name}
                  </div>
                </div>
                {/* Line 3: QR code*/}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end", /* Changed from space-between to flex-end */
                    alignItems: "flex-end", /* Changed from center to flex-end */
                    height: "33.3%",
                    padding: "0 5px 5px 5px", /* Added padding to align at bottom */
                    overflow: 'hidden',
                    visibility: hoveredItem === environment.id ? "visible" : "hidden",
                  }}
                >
                  {/* Delete icon */}
                  <IconButton
                    onClick={() => handleRemoveEnvironment(environment.id)}
                    style={{ marginRight: "0" }} 
                  >
                    <DeleteForeverIcon sx={{ fontSize: iconSize }} />
                  </IconButton>
                </div>
              </div>
            </div>
            {screenComponents}
          </React.Fragment>
        );
      })}
    </>
  );
}